// 官网每个页面的网址、标题和描述（SEO）。页面切换、<head> 标签（siteHead.js）和构建时生成静态页
// （scripts/build-site-pages.mjs）共用这一张表。英文无前缀，中文在 /zh 下，路径相同。
// 业务部门重新交付网站时，这个文件和 siteHead.js 原样保留。
import { landingContent } from './landingContent.js';

export const SITE_ORIGIN = 'https://bizsprite.com';
export const LANGS = ['en', 'zh'];

// view key（LandingPage 里的视图）→ 路径段。noindex：占位页，不进 sitemap。
export const PAGES = {
  home: { path: '' },
  products: { path: 'products' },
  cases: { path: 'cases' },
  pricing: { path: 'pricing' },
  about: { path: 'about' },
  contact: { path: 'contact' },
  blog: { path: 'insights' },
  help: { path: 'help' },
  resources: { path: 'resources', noindex: true },
  'ai-call': { path: 'ai-call', noindex: true },
  agreement: { path: 'agreement', noindex: true },
};

// 详情页：文章在 /insights/<id>，帮助文章在 /help/<id>。
const DETAIL = { 'blog-post': 'blog', 'help-article': 'help' };

const META = {
  en: {
    home: ['Bizsprite — AI phone answering for cross-border brands', 'A local North American number and an AI voice agent that answers every customer call 24/7, then follows up by SMS and tickets.'],
    products: ['Product — AI voice agent, SMS and tickets | Bizsprite', 'How Bizsprite answers calls on a local U.S. number, sends follow-up SMS and turns requests into tickets for your team.'],
    cases: ['Call demos — hear the AI agent | Bizsprite', 'Listen to real call demos: product questions, after-sales requests and after-hours calls handled by the Bizsprite AI agent.'],
    pricing: ['Pricing | Bizsprite', 'One monthly plan covers AI answering and a local U.S. number; call minutes and SMS are billed at actual usage.'],
    about: ['About us | Bizsprite', 'Bizsprite helps cross-border brands serve North American customers by phone, around the clock.'],
    contact: ['Contact us | Bizsprite', 'Talk to the Bizsprite team about AI phone answering for your brand. We reply within one business day.'],
    blog: ['Insights — cross-border customer service | Bizsprite', 'Field notes, how-to guides and product updates on AI voice service for cross-border e-commerce.'],
    help: ['Help center | Bizsprite', 'Set up your number, AI agent and billing, step by step.'],
    resources: ['Resources | Bizsprite', ''],
    'ai-call': ['AI call | Bizsprite', ''],
    agreement: ['Agreement | Bizsprite', ''],
  },
  zh: {
    home: ['Bizsprite — 跨境品牌的 AI 电话客服', '专属北美本地号码加 AI 语音客服，7×24 小时接听每一通客户来电，并用短信和工单跟进。'],
    products: ['产品 — AI 语音客服、短信与工单 | Bizsprite', '了解 Bizsprite 如何用美国本地号码接听来电、发送跟进短信，并把客户需求转成工单交给团队。'],
    cases: ['通话演示 — 听听 AI 客服怎么接 | Bizsprite', '收听真实通话演示：商品咨询、售后请求和下班后来电，看 Bizsprite AI 客服如何处理。'],
    pricing: ['定价 | Bizsprite', '一个月费覆盖 AI 接听和美国本地号码，通话分钟与短信按实际用量计费。'],
    about: ['关于我们 | Bizsprite', 'Bizsprite 帮助跨境品牌全天候用电话服务北美客户。'],
    contact: ['联系我们 | Bizsprite', '和 Bizsprite 团队聊聊你的品牌怎么用 AI 接电话，我们一个工作日内回复。'],
    blog: ['资讯中心 — 跨境客服实践 | Bizsprite', '跨境电商 AI 语音客服的一线观察、实操指南和产品更新。'],
    help: ['帮助中心 | Bizsprite', '一步步完成号码、AI 客服和充值的设置。'],
    resources: ['资源中心 | Bizsprite', ''],
    'ai-call': ['AI 通话 | Bizsprite', ''],
    agreement: ['协议 | Bizsprite', ''],
  },
};

const prefix = (lang) => (lang === 'zh' ? '/zh' : '');

export function pathFor(view, lang = 'en', id) {
  const parent = DETAIL[view];
  const page = PAGES[parent || view] || PAGES.home;
  const tail = [page.path, parent && id ? id : ''].filter(Boolean).join('/');
  return `${prefix(lang)}/${tail}` === '/zh/' ? '/zh' : `${prefix(lang)}/${tail}`;
}

export const allPosts = (lang) => {
  const b = (landingContent[lang] || landingContent.en).blog;
  const seen = new Map();
  [b.featured, ...(b.editorPicks || []), ...(b.allPosts || [])].forEach((p) => {
    if (p && p.id && !seen.has(p.id)) seen.set(p.id, { ...seen.get(p.id), ...p });
  });
  return [...seen.values()];
};

export const helpArticles = (lang) => (landingContent[lang] || landingContent.en).help.articles;

// '/zh/insights/after-hours-calls' → { lang: 'zh', view: 'blog-post', id: 'after-hours-calls' }; null if not a site page.
export function parsePath(pathname) {
  const parts = String(pathname || '/').split('/').filter(Boolean);
  const lang = parts[0] === 'zh' ? 'zh' : 'en';
  if (lang === 'zh') parts.shift();
  if (parts.length === 0) return { lang, view: 'home', id: null };
  const view = Object.keys(PAGES).find((key) => PAGES[key].path === parts[0]);
  if (!view || parts.length > 2) return null;
  if (parts.length === 1) return { lang, view, id: null };
  if (view === 'blog' && allPosts(lang).some((p) => p.id === parts[1])) return { lang, view: 'blog-post', id: parts[1] };
  if (view === 'help' && helpArticles(lang).some((a) => a.id === parts[1])) return { lang, view: 'help-article', id: parts[1] };
  return null;
}

// Everything <head> needs for one page.
export function pageMeta(view, lang = 'en', id) {
  const parent = DETAIL[view];
  const [baseTitle, baseDescription] = META[lang][parent || view] || META[lang].home;
  let title = baseTitle;
  let description = baseDescription;
  if (view === 'blog-post') {
    const post = allPosts(lang).find((p) => p.id === id) || {};
    title = `${post.title} | Bizsprite`;
    description = post.excerpt || baseDescription;
  } else if (view === 'help-article') {
    const article = helpArticles(lang).find((a) => a.id === id) || {};
    title = `${article.title} | ${baseTitle}`;
    description = article.summary || baseDescription;
  }
  const alternates = Object.fromEntries(LANGS.map((l) => [l, SITE_ORIGIN + pathFor(view, l, id)]));
  return {
    lang, title, description,
    canonical: alternates[lang],
    alternates,
    noindex: Boolean(PAGES[parent || view] && PAGES[parent || view].noindex),
  };
}

// Every indexable page in both languages: [{ view, lang, id, path }].
export function siteEntries() {
  const out = [];
  LANGS.forEach((lang) => {
    Object.keys(PAGES).filter((v) => !PAGES[v].noindex).forEach((view) => out.push({ view, lang, id: null, path: pathFor(view, lang) }));
    allPosts(lang).forEach((p) => out.push({ view: 'blog-post', lang, id: p.id, path: pathFor('blog-post', lang, p.id) }));
    helpArticles(lang).forEach((a) => out.push({ view: 'help-article', lang, id: a.id, path: pathFor('help-article', lang, a.id) }));
  });
  return out;
}

// Router paths that render the site (App.js); '/' is registered there already.
export function siteRoutePaths() {
  const tails = [...Object.values(PAGES).map((p) => p.path).filter(Boolean), 'insights/:id', 'help/:id'];
  return ['/zh', ...LANGS.flatMap((lang) => tails.map((t) => `${prefix(lang)}/${t}`))];
}
